var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { groupBy, map, merge, round, matches, get, compact } from 'lodash';
import { NodeType, GateType } from './types/common';
function encode(graph) {
    const { elements, name = 'my-template' } = graph;
    const sensors = findNodesByType(elements, NodeType.SENSOR).map(toSensor);
    const actuators = findNodesByType(elements, NodeType.ACTUATOR).map(toActuator);
    const notes = findNodesByType(elements, NodeType.NOTE).map(toNote);
    // relations are edges between "nodes" and "gates"
    const relations = toRelations(elements);
    // relations are edges between "nodes" and "actuators"
    const statesTriggers = toTriggers(elements);
    const stateChangeTriggers = toStageChangeTriggers(elements);
    // triggers are a merge of statesTriggers and stateChange triggers
    const triggers = mergeTriggers(statesTriggers, stateChangeTriggers);
    return {
        name,
        sensors,
        actuators,
        relations,
        notes,
        triggers
    };
}
function toNote(note) {
    const { position: { x, y }, data: { label } } = note;
    return {
        position: createPosition(x, y),
        text: label
    };
}
function toSensor(node) {
    const { x, y } = node.position;
    const { plug: { name, version }, configuration } = node.data;
    const { properties } = configuration, sensorConfiguration = __rest(configuration
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    , ["properties"]);
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return Object.assign({ 
        // only snakeCase / camelCase characters are allowed
        // @TODO: remove non-ascii characters since those are also not supported
        label: normalizeLabel(node.data.label), name,
        version, position: createPosition(x, y), properties }, sensorConfiguration);
}
function toActuator(node) {
    const { x, y } = node.position;
    const { plug, label, configuration } = node.data;
    const { properties } = configuration, actuatorConfiguration = __rest(configuration
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    , ["properties"]);
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return Object.assign({ label: normalizeLabel(label), name: plug.name, version: plug.version, position: createPosition(x, y), properties }, actuatorConfiguration);
}
// "relations" are gates
function toRelations(graph) {
    const gates = graph.filter(item => {
        return item.group === 'nodes' && item.data.type === 'gate';
    });
    return gates.map(gate => {
        const { x, y } = gate.position;
        const { parents, states } = graph
            .filter(item => item.group === 'edges')
            .reduce((acc, item) => {
            const isChild = item.data.target === gate.data.id;
            const targetGate = gates.find(g => g.data.id === item.data.target);
            if (!isChild)
                return acc;
            const sourceNode = graph.find(node => {
                return node.group === 'nodes' && node.data.id === item.data.source;
            });
            acc.parents.push(sourceNode.data.label);
            const states = item.data.states;
            const isGeneralGateTarget = matches({
                type: 'gate',
                gateType: GateType.GENERAL
            });
            if (isGeneralGateTarget(targetGate.data)) {
                states.forEach((_state, index) => {
                    acc.states[index] = acc.states[index] || [];
                    acc.states[index] = acc.states[index].concat(states[index]);
                });
            }
            else {
                acc.states[0] = acc.states[0] || [];
                acc.states[0].push(...item.data.states);
            }
            return acc;
        }, { parents: [], states: [] });
        return {
            label: normalizeLabel(gate.data.label),
            type: gate.data.gateType,
            position: createPosition(x, y),
            parentLabels: parents.map(normalizeLabel),
            combinations: states
        };
    });
}
// triggers are edges with "actuator" or "sensor" as a target
// we are only interested in states trigger type, we will use `toStageChangeTriggers`
// to discover the stateChange triggers from our MemoryBlock nodes
function toTriggers(graph) {
    return graph
        .filter(item => item.group === 'edges')
        .reduce((acc, edge) => {
        var _a;
        const sourceNode = findNodeById(graph, edge.data.source);
        const targetNode = findNodeById(graph, edge.data.target);
        const sourceType = sourceNode.data.type;
        const targetType = targetNode.data.type;
        const isFromMemoryBlock = sourceType === NodeType.MEMORY_BLOCK ||
            targetType === NodeType.MEMORY_BLOCK;
        const targetsNodeOrActuator = targetType === NodeType.ACTUATOR || targetType === NodeType.SENSOR;
        if (isFromMemoryBlock || !targetsNodeOrActuator) {
            return acc;
        }
        const triggerType = edge.data.triggerType;
        const statesTrigger = triggerType === 'states' ? edge.data.states : undefined;
        const trigger = {
            sourceLabel: normalizeLabel(sourceNode.data.label),
            destinationLabel: normalizeLabel((_a = targetNode === null || targetNode === void 0 ? void 0 : targetNode.data) === null || _a === void 0 ? void 0 : _a.label),
            invocationPolicy: get(targetNode, 'data.configuration.policy', 0),
            statesTrigger
        };
        acc = acc.concat(trigger);
        return acc;
    }, []);
}
function toStageChangeTriggers(elements) {
    const memoryBlocks = findNodesByType(elements, NodeType.MEMORY_BLOCK);
    const blocks = memoryBlocks.map((memoryBlock) => {
        var _a;
        const { sourceId, targetId, stateFrom, stateTo } = memoryBlock.data;
        // incomplete stateChange trigger, skip
        if (!sourceId || !targetId) {
            return null;
        }
        const sourceNode = findNodeById(elements, sourceId);
        const targetNode = findNodeById(elements, targetId);
        return {
            sourceLabel: normalizeLabel(sourceNode.data.label),
            destinationLabel: normalizeLabel((_a = targetNode === null || targetNode === void 0 ? void 0 : targetNode.data) === null || _a === void 0 ? void 0 : _a.label),
            invocationPolicy: get(targetNode, 'data.configuration.policy', 0),
            stateChangeTrigger: {
                stateFrom,
                stateTo
            },
            statesTrigger: undefined
        };
    });
    return compact(blocks);
}
// we have previously created multiple triggers, they have to be combined so we
// have a single trigger with both `statesTrigger` and `stateChangeTrigger` combined if applicable
function mergeTriggers(triggers, stateChangeTriggers) {
    const grouped = groupBy([...triggers, ...stateChangeTriggers], trigger => {
        return trigger.sourceLabel + trigger.destinationLabel;
    });
    return map(grouped, triggers => {
        const combinedTrigger = triggers.reduce((acc, trigger) => {
            return merge(acc, trigger);
            // eslint-disable-next-line @typescript-eslint/prefer-reduce-type-parameter, @typescript-eslint/consistent-type-assertions
        }, {});
        return combinedTrigger;
    });
}
function findNodesByType(elements, type) {
    return elements.filter(item => item.group === 'nodes' && item.data.type === type);
}
function findNodeById(graph, id) {
    return graph.find(node => node.group === 'nodes' && node.data.id === id);
}
// The Engine does not support and refuses coordinates that are floats
function createPosition(x, y) {
    return [round(x), round(y)];
}
// test the label for illegal characters not allowed by the simplified BN definition
function normalizeLabel(label) {
    const allowedCharacters = /[^0-9a-zA-Z_-]+/g;
    if (allowedCharacters.test(label)) {
        throw new Error(`illegal character found in node '${label}', allowed characters are [0-9a-zA-Z_-]`);
    }
    return label;
}
export default encode;
//# sourceMappingURL=encode.js.map